import axios from 'axios';
import { API_BASE_URL, REQUEST_TIMEOUT_MS } from './api';

export const ADMIN_TOKEN_KEY = 'admin_token';

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export interface AdminUserInfo {
  id: number;
  username: string;
  created_at: string;
  last_login_at?: string | null;
  is_active: boolean;
}

export interface AdminLoginResponse {
  access_token: string;
  token_type: string;
  admin: AdminUserInfo;
}

export interface TimeSeriesPoint {
  date: string;
  count: number;
}

export interface UserCountItem {
  user_id: number;
  email: string;
  count: number;
}

export interface TimeSeriesAverage {
  date: string;
  average: number;
}

export interface ProcessingMetrics {
  average_processing_seconds: number;
  average_processing_seconds_24h: number;
  average_file_size_bytes: number;
  average_file_size_bytes_24h: number;
  processing_time_over_time: TimeSeriesAverage[];
  file_size_over_time: TimeSeriesAverage[];
  average_char_count: number;
  average_char_count_24h: number;
  char_count_over_time: TimeSeriesAverage[];
  sample_size: number;
  correlation_samples?: Array<{
    seconds: number;
    size_bytes: number;
    char_count: number;
    document_id: number;
    completed_at: string;
  }>;
  processing_table?: Array<{
    document_id: number;
    timestamp: string;
    completed_at?: string | null;
    user_role?: string | null;
    processing_seconds?: number | null;
    char_count: number;
    file_size_bytes: number;
    file_type?: string | null;
    preprocess_ms?: number | null;
    stage1_ms?: number | null;
    stage2_ms?: number | null;
    qa_avg_response_time_ms?: number | null;
    ai_processing_duration_seconds?: number | null;
    language?: string | null;
  }>;
}

export interface LanguageStats {
  users_by_language: {
    pt: number;
    en: number;
  };
  anonymous_by_language: {
    pt: number;
    en: number;
  };
  total_pt: number;
  total_en: number;
  users_over_time?: { date: string; pt: number; en: number }[];
  anonymous_over_time?: { date: string; pt: number; en: number }[];
}

export interface AITrackingStats {
  total_documents_analyzed: number;
  documents_analyzed_last_7_days: number;
  processing_tokens: {
    total: number;
    verification: number;
    summary: number;
    risk: number;
    recommendations: number;
    clauses: number;
  };
  processing_costs_cents: {
    processing: number;
    questions: number;
    total: number;
  };
  questions_stats: {
    total_tokens: number;
    total_cost_cents: number;
    avg_response_time_ms: number;
  };
  avg_processing_time_seconds: number;
}

export interface RealtimeDocuments {
  processing: number;
  completed: number;
  failed: number;
  total: number;
  timestamp: string;
}

export interface AIModelConfig {
  model_summary?: string | null;
  model_risks?: string | null;
  model_qa?: string | null;
  model_contract_type?: string | null;
  model_notifications?: string | null;
  updated_at?: string | null;
  allowed_models: string[];
  default_models?: Record<string, string>;
}

export type AIModelConfigUpdate = Partial<Omit<AIModelConfig, 'allowed_models' | 'updated_at'>>;

export interface LogsResponse {
  path: string;
  lines: string[];
}

export interface RealtimeDocuments {
  processing: number;
  completed: number;
  failed: number;
  total: number;
  timestamp: string;
}

export interface AdminStatsResponse {
  total_users: number;
  total_admins: number;
  total_documents: number;
  total_completed_documents: number;
  total_processing_documents: number;
  total_failed_documents: number;
  total_ai_requests: number;
  completion_rate: number;
  registrations_last_7_days: number;
  registrations_last_30_days: number;
  uploads_last_7_days: number;
  uploads_last_30_days: number;
  ai_requests_last_7_days: number;
  active_users_last_30_days: number;
  registrations_over_time: TimeSeriesPoint[];
  uploads_over_time: TimeSeriesPoint[];
  ai_requests_over_time: TimeSeriesPoint[];
  anonymous_uploads_last_7_days?: number;
  anonymous_uploads_over_time?: TimeSeriesPoint[];
  unsupported_documents_last_7_days?: number;
  unsupported_documents_over_time?: TimeSeriesPoint[];
  uploads_by_user: UserCountItem[];
  ai_requests_by_user: UserCountItem[];
  admin_users: AdminUserInfo[];
  processing_metrics?: ProcessingMetrics;
  ai_tracking?: AITrackingStats;
  language_stats?: LanguageStats;
}

export interface CampaignStats {
  source: string;
  visitors: number;
  logins: number;
  registered_users: number;
  anonymous_users: number;
  anonymous_uploads: number;
  registered_uploads: number;
  analyzed_contracts: number;
  paid_premium: number;
  paid_platinum: number;
  visitor_to_registration: number;
  registration_to_upload: number;
  upload_to_paid: number;
  total_revenue_cents: number;
  first_seen?: string;
  last_activity?: string;
  top_landing_pages: string[];
  users_pt?: number;
  users_en?: number;
  is_hidden?: boolean;
  avg_session_seconds?: number;
  // Upload funnel metrics
  funnel_started?: number;
  funnel_role_selected?: number;
  funnel_file_selected?: number;
  funnel_upload_completed?: number;
  funnel_role_dropoff_pct?: number;
  funnel_file_dropoff_pct?: number;
}

export interface CampaignStatsResponse {
  campaigns: CampaignStats[];
  generated_at: string;
  visits_over_time?: TimeSeriesPoint[];
  hourly_avg_visits?: Array<{ hour: number; average_users?: number; avg?: number; count?: number }>;
}

export interface AdminUserListItem {
  id: number;
  email: string;
  created_at: string;
  document_count: number;
  ai_requests: number;
  last_upload_at?: string | null;
  last_active_at?: string | null;
  subscription_tier: string;
  email_verified: boolean;
  email_verified_at?: string | null;
}

export interface AdminUsersResponse {
  users: AdminUserListItem[];
}

export interface UpdateUserTierRequest {
  subscription_tier: string;
}

export interface UpdateUserTierResponse {
  message: string;
  user_id: number;
  new_tier: string;
}

export interface UserDetailedStats {
  user_id: number;
  email: string;
  full_name?: string | null;
  created_at: string;
  subscription_tier: string;
  total_documents: number;
  total_ai_requests: number;
  uploads_over_time: TimeSeriesPoint[];
  ai_requests_over_time: TimeSeriesPoint[];
}

export interface DeleteUserResponse {
  message: string;
  user_id: number;
}

export interface FeedbackItem {
  id: number;
  user_id: number | null;
  email: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
  user_name: string | null;
  has_attachment?: boolean;
  attachment_filename?: string | null;
  attachment_content_type?: string | null;
  attachment_size?: number | null;
  attachments?: Array<{
    id: number;
    attachment_filename?: string | null;
    attachment_content_type?: string | null;
    attachment_size?: number | null;
    created_at: string;
  }>;
}

export interface FeedbackEmailConfig {
  id: number;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FunnelMetrics {
  visitors: number;
  signups: number;
  uploads: number;
  questions: number;
  pricing_clicks: number;
  payments: number;
  conversion_rates: {
    visitor_to_signup: number;
    signup_to_upload: number;
    upload_to_question: number;
    question_to_pricing: number;
    pricing_to_payment: number;
    overall_conversion: number;
  };
}

export interface AIQualityMetrics {
  average_ai_confidence: number;
  average_user_rating: number;
  analysis_completion_rate: number;
  total_questions_asked: number;
  total_analyses_started: number;
  total_analyses_completed: number;
}

export interface FailureMetrics {
  total_failures: number;
  failure_breakdown: Record<string, number>;
  upload_failures: number;
  payment_failures: number;
  user_abandons: number;
  failures_over_time?: TimeSeriesPoint[];
  failure_codes_over_time?: Array<{
    date: string;
    external?: number;
    internal?: number;
    user?: number;
    upload?: number;
  }>;
  category_counts?: {
    external?: number;
    internal?: number;
    user?: number;
    upload?: number;
  };
  recent_failures?: Array<{
    document_id?: number | null;
    user_id?: number | null;
    reason: string;
    occurred_at?: string | null;
  }>;
  error_logs?: ErrorLogEntry[];
}

export interface ErrorLogEntry {
  timestamp?: string | null;
  logger?: string | null;
  level?: string | null;
  message: string;
  code?: string | null;
  stack?: string | null;
}

export interface MetricsDashboard {
  period_days: number;
  users: {
    total_all_time: number;
    new_this_period: number;
    active_this_period: number;
    paid: number;
  };
  registration: {
    total: number;
    by_method: {
      google: number;
      email: number;
      google_pct: number;
      email_pct: number;
    };
    by_source: Record<string, number>;
    from_anonymous: number;
    from_anonymous_pct: number;
  };
  traffic: {
    total: number;
    by_source: Record<string, number>;
    by_page: Record<string, number>;
    by_country: Record<string, number>;
    by_region: Record<string, number>;
    by_city: Record<string, number>;
    by_browser: Record<string, number>;
    by_os: Record<string, number>;
    by_device: Record<string, number>;
    window_days: number;
    page_filter?: string | null;
    avg_visits_per_user_by_page?: Record<string, number>;
  };
  funnel: FunnelMetrics;
  upload_funnel: {
    started: number;
    role_selected: number;
    file_selected: number;
    upload_completed: number;
    conversion_rates: {
      started_to_role: number;
      role_to_file: number;
      file_to_upload: number;
      overall: number;
    };
    dropoff_rates: {
      role_dropoff: number;
      file_dropoff: number;
      upload_dropoff: number;
    };
  };
  post_upload_funnel: {
    uploaded: number;
    viewed_risks: number;
    asked_questions: number;
    enabled_notifications: number;
    conversion_rates: {
      upload_to_risks: number;
      upload_to_questions: number;
      upload_to_notifications: number;
      risks_to_questions: number;
      questions_to_notifications: number;
    };
  };
  limit_encounter: {
    hit_limit: number;
    saw_paywall: number;
    clicked_pricing: number;
    completed_payment: number;
    conversion_rates: {
      limit_to_paywall: number;
      limit_to_pricing: number;
      limit_to_payment: number;
      pricing_to_payment: number;
    };
  };
  ai_quality: AIQualityMetrics;
  failures: FailureMetrics;
  behavior: {
    signups: {
      total: number;
      landing: number;
      google: number;
      from_anonymous: number;
      landing_pct: number;
      google_pct: number;
      anonymous_pct: number;
    };
    cta_clicks: {
      total: number;
      by_location: Record<string, number>;
    };
    anonymous: {
      sessions_started: number;
      sessions_completed: number;
      sessions_converted: number;
      sessions_no_followup: number;
      completion_rate: number;
      conversion_rate: number;
    };
    paid: {
      paid_users_total: number;
      paid_users_new: number;
      paid_upgrades: number;
      paid_conversion_rate: number;
    };
    tiers: {
      free: number;
      premium: number;
      platinum: number;
    };
  };
  mvp_metrics: {
    users_uploading_contract_pct: number;
    users_asking_questions_pct: number;
    users_clicking_pricing_pct: number;
    users_willing_to_pay_pct: number;
    average_ai_confidence: number;
    analysis_completion_rate_pct: number;
  };
  goals: {
    users_uploading_contract_target: number;
    users_asking_questions_target: number;
    users_clicking_pricing_target: number;
    users_willing_to_pay_target: number;
    average_ai_confidence_target: number;
    analysis_completion_rate_target: number;
  };
  insights: {
    problem_is_real: boolean;
    product_solving_it: boolean;
    users_will_pay: boolean;
  };
  language?: LanguageStats;
  screen_time?: {
    overall_avg_seconds: number;
    median_seconds?: number;
    by_page: Record<string, number>;
    by_section: Record<string, number>;
    samples: number;
    window_days?: number;
  };
}

export interface RealtimeDocuments {
  processing: number;
  completed: number;
  failed: number;
  total: number;
  timestamp: string;
}

export const adminAPI = {
  login: (username: string, password: string) =>
    adminApi.post<AdminLoginResponse>('/admin/login', { username, password }),
  stats: (processingTableLimit?: number, days: number = 30) =>
    adminApi.get<AdminStatsResponse>('/admin/stats', {
      params: {
        ...(processingTableLimit !== undefined ? { processing_table_limit: processingTableLimit } : {}),
        days,
      }
    }),
  users: () => adminApi.get<AdminUsersResponse>('/admin/users'),
  updateUserTier: (userId: number, tier: string) =>
    adminApi.put<UpdateUserTierResponse>(`/admin/users/${userId}/subscription-tier`, { subscription_tier: tier }),
  deleteUser: (userId: number) =>
    adminApi.delete<DeleteUserResponse>(`/admin/users/${userId}`),
  getUserStats: (userId: number) =>
    adminApi.get<UserDetailedStats>(`/admin/users/${userId}/stats`),

  // Feedback management
  getFeedbacks: (unreadOnly: boolean = false) =>
    adminApi.get<FeedbackItem[]>('/feedback/list', { params: { unread_only: unreadOnly } }),
  markFeedbackRead: (feedbackId: number) =>
    adminApi.patch<FeedbackItem>(`/feedback/${feedbackId}/read`),
  deleteFeedback: (feedbackId: number) =>
    adminApi.delete<{ message: string; id: number }>(`/feedback/${feedbackId}`),
  getFeedbackAttachment: (feedbackId: number) =>
    adminApi.get(`/feedback/${feedbackId}/attachment`, { responseType: 'blob' }),
  getFeedbackAttachmentById: (feedbackId: number, attachmentId: number) =>
    adminApi.get(`/feedback/${feedbackId}/attachments/${attachmentId}`, { responseType: 'blob' }),
  getFeedbackEmailConfigs: () =>
    adminApi.get<FeedbackEmailConfig[]>('/feedback/configs'),
  addFeedbackEmailConfig: (email: string) =>
    adminApi.post<FeedbackEmailConfig>('/feedback/configs', { email }),
  updateFeedbackEmailConfig: (configId: number, isActive: boolean) =>
    adminApi.patch<FeedbackEmailConfig>(`/feedback/configs/${configId}`, { is_active: isActive }),
  deleteFeedbackEmailConfig: (configId: number) =>
    adminApi.delete(`/feedback/configs/${configId}`),

  // Metrics endpoints
  getMetricsDashboard: (days: number = 30) =>
    adminApi.get<MetricsDashboard>('/metrics/dashboard', { params: { days } }),
  getFunnelMetrics: (days: number = 30) =>
    adminApi.get<FunnelMetrics>('/metrics/funnel', { params: { days } }),
  getAIQualityMetrics: (days: number = 30) =>
    adminApi.get<AIQualityMetrics>('/metrics/ai-quality', { params: { days } }),
  getFailureMetrics: (days: number = 30) =>
    adminApi.get<FailureMetrics>('/metrics/failures', { params: { days } }),
  getRealtimeDocuments: (days: number = 7) =>
    adminApi.get<RealtimeDocuments>('/metrics/realtime-documents', { params: { days } }),
  getLogs: (limit: number = 200, level?: string) =>
    adminApi.get<LogsResponse>('/admin/logs', { params: { limit, level } }),
  getCampaigns: (days: number = 90) =>
    adminApi.get<CampaignStatsResponse>('/admin/campaigns', { params: { days } }),
  hideCampaign: (source: string) =>
    adminApi.post('/admin/campaigns/hide/' + encodeURIComponent(source)),
  unhideCampaign: (source: string) =>
    adminApi.delete('/admin/campaigns/hide/' + encodeURIComponent(source)),
  getAIModelConfig: () => adminApi.get<AIModelConfig>('/admin/ai-model-config'),
  updateAIModelConfig: (payload: AIModelConfigUpdate) =>
    adminApi.put<AIModelConfig>('/admin/ai-model-config', payload),
};

export default adminApi;
